import { computed, type ComputedRef, type Ref } from 'vue'
import type { CrosschainBuyViewState } from '@/features/crosschain-buy/buy-flow/view-state'
import type { SourceChainType, WalletSourceToken } from '@/features/crosschain-buy/source-tokens'
import type { RouteUxStatus } from '@/features/crosschain-buy/buy-flow/ui/composables/useRouteUxStatus'
import { RANGO_ENABLED } from '@/features/crosschain-buy/buy-flow/presentation/route-config'
import { ErrorClass, FlowStatus, TonLegSubStatus } from '@/features/crosschain-buy/domain/flow'
import {
  getRecoveryHint,
  getUserMessage,
} from '@/features/crosschain-buy/buy-flow/presentation/errors'
import {
  isRetryableUsdtToGramFailure,
  isTonToSpottyContinuationReady,
} from '@/features/crosschain-buy/domain/ton-progress'

export interface UseBuySpottyCopyDeps {
  state: Ref<CrosschainBuyViewState>
  needsTonReconnect: Ref<boolean> | ComputedRef<boolean>
  needsTonBackendReauth: Ref<boolean> | ComputedRef<boolean>
  routeUxStatus: Ref<RouteUxStatus> | ComputedRef<RouteUxStatus>
  sourceWalletAddress: Ref<string> | ComputedRef<string>
  selectedToken: Ref<WalletSourceToken | undefined> | ComputedRef<WalletSourceToken | undefined>
  sourceAmount: Ref<string>
  amountValidationError: Ref<string | null>
  spendableBalanceLabel: Ref<string | null> | ComputedRef<string | null>
  sourceChainType: Ref<SourceChainType> | ComputedRef<SourceChainType>
  selectedSourceTokenAddress: Ref<string | null | undefined>
  availableSourceTokens: Ref<WalletSourceToken[]> | ComputedRef<WalletSourceToken[]>
  isFlowBusy: Ref<boolean>
  evmChainMismatch:
    | Ref<{ actualLabel: string; targetLabel: string } | null>
    | ComputedRef<{
        actualLabel: string
        targetLabel: string
      } | null>
}

interface PreparedFinalCommandView {
  expiresAtMs: number
  swapId: string
  stepId: string
  walletRequestId: string
  walletAddress: string
  routeFingerprint: string
}

const finalTonRouteFingerprint = (state: CrosschainBuyViewState): string => {
  const quote = state.quote
  const bridgeTxHash = [...state.steps]
    .reverse()
    .find((step) => step.type === 'bridge' && !!step.txHash)?.txHash
  return [
    'ton_to_spotty',
    quote?.provider ?? '',
    quote?.toToken ?? '',
    quote?.toAmount ?? '',
    state.fromToken,
    state.fromAmount,
    bridgeTxHash ?? '',
  ].join('|')
}

const IN_PROGRESS_STATUSES = new Set<RouteUxStatus>([
  'processing',
  'success',
  'error_recoverable',
  'error_blocking',
])

const getBuyFlowUserMessage = (error: ErrorClass | null): string | null =>
  error === ErrorClass.BRIDGE_CREATE_FAILED
    ? 'Bridge step couldn’t reach the network. Your USDT is safe in your wallet — tap Continue to finish the bridge.'
    : getUserMessage(error)

export function useBuySpottyCopy(deps: UseBuySpottyCopyDeps) {
  const {
    state,
    needsTonReconnect,
    needsTonBackendReauth,
    routeUxStatus,
    sourceWalletAddress,
    selectedToken,
    sourceAmount,
    amountValidationError,
    sourceChainType,
    selectedSourceTokenAddress,
    availableSourceTokens,
    isFlowBusy,
    evmChainMismatch,
  } = deps

  const walletAssistName = computed(() => state.value.walletAssist?.walletName ?? 'your wallet')

  const isFinalTonRetry = computed(() => {
    const finalStep = state.value.steps.find((step) => step.type === 'ton_to_spotty')
    return (
      state.value.status === FlowStatus.ERROR &&
      state.value.lastError === ErrorClass.TON_TX_FAILED &&
      isTonToSpottyContinuationReady(state.value) &&
      finalStep?.status === 'failed' &&
      !finalStep.txHash
    )
  })

  const isFinalTonRetryCommandFresh = computed(() => {
    const finalStep = state.value.steps.find((step) => step.type === 'ton_to_spotty')
    const prepared = (
      state.value as CrosschainBuyViewState & {
        preparedTonToSpotty?: PreparedFinalCommandView | null
      }
    ).preparedTonToSpotty
    return (
      state.value.tonPreparationStatus === 'ready' &&
      !!prepared &&
      prepared.expiresAtMs > Date.now() &&
      prepared.swapId === state.value.currentSwapId &&
      prepared.stepId === finalStep?.stepId &&
      prepared.walletRequestId === finalStep.walletRequestId &&
      prepared.walletAddress === state.value.ton.address &&
      prepared.routeFingerprint === finalTonRouteFingerprint(state.value)
    )
  })

  const walletAssistVisible = computed(() => {
    if (!state.value.walletAssist) return false
    return isFlowBusy.value || state.value.lastError === 'swap_timeout'
  })

  const walletAssistMessage = computed(() => {
    const assist = state.value.walletAssist
    if (!assist) return ''

    if (state.value.lastError === 'swap_timeout') {
      return `${walletAssistName.value} may still have a pending request. Open the wallet, complete or reject it, then try again.`
    }

    return assist.pendingAction === 'approval'
      ? `${walletAssistName.value} needs you to approve the token spend before the bridge can continue.`
      : `${walletAssistName.value} needs you to sign the bridge transaction to continue.`
  })

  const tokensWithBalanceCount = computed(
    () => availableSourceTokens.value.filter((t) => t.hasBalance).length
  )

  const friendlyErrorMessage = computed(() => {
    if (evmChainMismatch.value) {
      return `Wallet is on ${evmChainMismatch.value.actualLabel}. Switch to ${evmChainMismatch.value.targetLabel} to continue.`
    }
    const lastError = state.value.lastError

    if (lastError === ErrorClass.TON_SYNC_REQUIRED) return null
    if (isFinalTonRetry.value) {
      return 'The final TON transaction was not sent. You can retry it safely.'
    }

    // Connection cards already communicate wallet errors outside an active failure screen.
    const isWalletClassError =
      lastError === ErrorClass.WALLET_NOT_CONNECTED || lastError === ErrorClass.WALLET_SESSION_DEAD
    if (isWalletClassError && state.value.status !== FlowStatus.ERROR) {
      return null
    }

    // Suppress the AMOUNT_TOO_SMALL banner when an inline (chain-aware)
    // amountValidationError is already presenting the canonical message —
    // the inline error wins. Other error classes still render normally.
    if (lastError === ErrorClass.AMOUNT_TOO_SMALL && amountValidationError.value) {
      return null
    }

    return getBuyFlowUserMessage(lastError)
  })

  const recoveryHint = computed(() => {
    if (amountValidationError.value) {
      return amountValidationError.value
    }
    return state.value.lastError === ErrorClass.TON_SYNC_REQUIRED
      ? null
      : getRecoveryHint(state.value.lastError)
  })

  const showSolanaProviderSelector = computed(() => {
    if (!RANGO_ENABLED) return false
    if (sourceChainType.value !== 'solana') return false
    // Show selector when on Solana and user has tokens selected (regardless of quote/error state)
    // This allows switching providers even when no route was found
    if (!selectedSourceTokenAddress.value) return false
    return ['wallet_gate', 'quote_ready', 'quote_loading', 'error'].includes(state.value.status)
  })

  const isTonToSpottyPending = computed(() => isTonToSpottyContinuationReady(state.value))

  // Continue stays disabled until the balance is settled and warmed off the gesture path.
  const isPreparingFinalSwap = computed(
    () => state.value.tonLegSubStatus === TonLegSubStatus.SETTLING
  )

  const isPreparingTonCommand = computed(
    () =>
      (state.value.status === FlowStatus.DESTINATION_SWAP_READY || isTonToSpottyPending.value) &&
      state.value.tonPreparationStatus === 'preparing'
  )

  const tonPreparationFailed = computed(
    () =>
      (state.value.status === FlowStatus.DESTINATION_SWAP_READY || isTonToSpottyPending.value) &&
      state.value.tonPreparationStatus === 'failed'
  )

  const isPreparedLayerswapDesktopHandoff = computed(
    () => state.value.isLayerswapDesktopHandoffReady === true
  )

  const continueLabel = computed(() => {
    if (evmChainMismatch.value) return `Switch to ${evmChainMismatch.value.targetLabel}`
    // Phantom deeplink return path: TonConnect session died while the user
    // was out of the tab but state.ton.address is still persisted. Normal
    // "Start Swap" would error immediately on isTonWalletConnected()=false.
    if (needsTonReconnect.value) {
      return isRetryableUsdtToGramFailure(state.value)
        ? 'Reconnect TON to retry GRAM swap'
        : 'Reconnect TON to finish swap'
    }

    if (needsTonBackendReauth.value) {
      return 'Re-authenticate TON wallet'
    }

    if (state.value.lastError === ErrorClass.TON_SYNC_REQUIRED) {
      return 'Confirming TON transaction…'
    }

    if (state.value.lastError === ErrorClass.SOLANA_TX_UNCERTAIN) {
      return 'Confirming Solana transaction…'
    }

    if (state.value.lastError === ErrorClass.SOLANA_TX_FAILED) {
      return 'Retry'
    }

    if (isPreparedLayerswapDesktopHandoff.value) {
      return 'Continue'
    }

    if (isFinalTonRetry.value) {
      if (isFinalTonRetryCommandFresh.value) return 'Retry final swap'
      if (
        state.value.tonPreparationStatus === 'failed' ||
        state.value.tonPreparationStatus === 'ready'
      ) {
        return 'Retry preparation'
      }
      return 'Preparing retry…'
    }

    // Balance warming precedes the generic in-progress label.
    if (isPreparingFinalSwap.value) {
      return 'Preparing final swap…'
    }

    if (isPreparingTonCommand.value) {
      return 'Preparing next step…'
    }

    if (tonPreparationFailed.value) {
      return 'Retry preparation'
    }

    // The final wallet request needs a fresh user gesture after USDT→TON settles.
    if (isTonToSpottyPending.value && state.value.tonPreparationStatus === 'ready') {
      return 'Continue to final swap'
    }

    // BRIDGE_CREATE_FAILED is registered only after the recovery snapshot is persisted.
    if (state.value.lastError === ErrorClass.BRIDGE_CREATE_FAILED) {
      return 'Continue'
    }

    if (state.value.evmBridgeCheckpoint?.status === 'invoked') {
      return 'Confirm in wallet…'
    }

    if (state.value.evmBridgeCheckpoint?.status === 'reconciling') {
      return 'Checking transaction…'
    }

    if (
      state.value.status === FlowStatus.QUOTE_READY &&
      state.value.lastError === ErrorClass.BRIDGE_FAILED
    ) {
      return 'Retry bridge'
    }

    if (
      state.value.status === FlowStatus.QUOTE_READY &&
      state.value.evmApprovalCheckpoint?.completed
    ) {
      return 'Approval complete — Continue bridge'
    }

    if (state.value.status === FlowStatus.QUOTE_READY && state.value.evmApprovalCheckpoint) {
      return 'Check approval status'
    }

    if (isRetryableUsdtToGramFailure(state.value)) {
      return 'Retry GRAM swap'
    }

    // If swap is already in progress (past quote phase), skip token/amount checks
    if (IN_PROGRESS_STATUSES.has(routeUxStatus.value)) {
      switch (routeUxStatus.value) {
        case 'success':
          return 'Buy More SPOTTY'
        case 'processing':
          return 'Processing...'
        case 'error_recoverable':
          return 'Try Different Amount/Token'
        case 'error_blocking':
          return 'Unable to create route'
        case 'empty':
        case 'invalid_amount':
        case 'invalidated':
        case 'loading':
        case 'ready_preview':
        case 'ready_executable':
        default:
          break
      }
    }

    if (state.value.status === 'destination_swap_ready') {
      if (state.value.tonPreparationStatus !== 'ready') return 'Prepare final swap'
      return 'Continue'
    }

    // Wallet connection states
    if (!sourceWalletAddress.value)
      return sourceChainType.value === 'solana' ? 'Connect Solana Wallet' : 'Connect EVM Wallet'
    if (!state.value.ton.address) return 'Connect TON Wallet'

    // Selection states
    if (!selectedToken.value) return 'Select Token'
    if (amountValidationError.value) {
      return Number(sourceAmount.value) > 0 ? 'Adjust amount to continue' : 'Insufficient balance'
    }
    if (Number(sourceAmount.value) <= 0) return 'Enter Amount'

    // Route states using UX status
    switch (routeUxStatus.value) {
      case 'empty':
        return 'Enter amount to see route'
      case 'invalid_amount':
        return 'Adjust amount to continue'
      case 'invalidated':
        return 'Updating route...'
      case 'loading':
        return 'Finding best route...'
      case 'ready_preview':
        return 'Preparing route...'
      case 'ready_executable':
        return 'Start Swap'
      case 'success':
        return 'Buy More SPOTTY'
      case 'processing':
        return 'Processing...'
      case 'error_recoverable':
        return 'Try Different Amount/Token'
      case 'error_blocking':
        return 'Unable to create route'
      default:
        return 'Buy SPOTTY'
    }
  })

  const isContinueDisabled = computed(() => {
    if (state.value.evmBridgeCheckpoint?.status === 'reconciling') return true
    if (isFlowBusy.value) return true
    if (evmChainMismatch.value) return false

    // Zombie TON state: button must stay clickable so the user can reopen
    // TonConnect and reconnect the same wallet to finish step 2. Wins over the
    // SETTLING gate below (mirrors continueLabel's ordering).
    if (needsTonReconnect.value) return false

    if (needsTonBackendReauth.value) return false

    if (state.value.lastError === ErrorClass.TON_SYNC_REQUIRED) return true
    if (state.value.lastError === ErrorClass.SOLANA_TX_UNCERTAIN) return true

    if (isPreparedLayerswapDesktopHandoff.value) return false

    if (isFinalTonRetry.value) {
      if (isFinalTonRetryCommandFresh.value) return false
      return (
        state.value.tonPreparationStatus !== 'ready' &&
        state.value.tonPreparationStatus !== 'failed'
      )
    }

    // Two-phase STON.fi pause: keep DISABLED while the balance warms+settles,
    // so tonToSpotty's balance read never lands on the Continue-tap gesture.
    if (isPreparingFinalSwap.value) return true

    if (isPreparingTonCommand.value) return true

    if (tonPreparationFailed.value) return false

    // Two-phase STON.fi: button must stay clickable to fire tonToSpotty inside
    // the user's gesture. routeUxStatus would otherwise be 'processing'
    // and disable it (DESTINATION_SWAPPING ∈ ACTIVE_BRIDGE_STATUSES).
    if (isTonToSpottyPending.value && state.value.tonPreparationStatus === 'ready') return false

    // If swap is already in progress, skip token/amount checks
    if (IN_PROGRESS_STATUSES.has(routeUxStatus.value)) {
      return routeUxStatus.value === 'processing' || routeUxStatus.value === 'error_blocking'
    }

    if (state.value.status === 'destination_swap_ready') {
      return false
    }

    // Wallet connections are always enabled
    if (!sourceWalletAddress.value) return false
    if (!state.value.ton.address) return false

    // Selection states
    if (!selectedToken.value) return true
    if (Number(sourceAmount.value) <= 0) return true

    // Amount validation errors
    if (amountValidationError.value) return true

    // Route states - disable when not ready to execute
    switch (routeUxStatus.value) {
      case 'empty':
      case 'invalid_amount':
      case 'invalidated':
      case 'loading':
      case 'ready_preview':
        return true
      case 'ready_executable':
      case 'success':
      case 'error_recoverable':
        return false
      case 'processing':
        return true
      case 'error_blocking':
        return true
      default:
        return true
    }
  })

  return {
    continueLabel,
    isContinueDisabled,
    walletAssistName,
    walletAssistVisible,
    walletAssistMessage,
    tokensWithBalanceCount,
    friendlyErrorMessage,
    recoveryHint,
    showSolanaProviderSelector,
  }
}
